/**
 * Pure helpers for the super-user Test chat page (routes/admin/TestChat.svelte).
 *
 * The conversation lives only in the page: each turn sends the whole thread so
 * far, and the server (str_autoresponder/simulator.py) is stateless. Kept here,
 * out of the component, so the thread rules are unit-testable.
 */

/** How each simulated outcome is labelled, and the colour token it's shown in. */
export const OUTCOMES = {
  auto_send: { label: 'Would send automatically', tone: 'success' },
  suggest: { label: 'Draft for host review', tone: 'warning' },
  escalate: { label: 'Escalated to host', tone: 'danger' },
  delay: { label: 'Held briefly, then host review', tone: 'info' },
  no_response_needed: { label: 'No reply needed', tone: 'muted' },
  channel_system_message: { label: 'Channel notice — no draft', tone: 'muted' },
};

export function outcomeMeta(outcome) {
  return OUTCOMES[outcome] ?? { label: outcome ?? 'Unknown', tone: 'muted' };
}

/**
 * The conversation the server should see before entry `index`: every earlier
 * guest/host message that actually landed (not an errored or pending send).
 */
export function historyBefore(entries, index) {
  return entries
    .slice(0, index)
    .filter((e) => e.status !== 'error')
    .map((e) => ({ sender_type: e.kind, body: e.body }));
}

/** Index of the last guest entry, or -1. */
export function lastGuestIndex(entries) {
  for (let i = entries.length - 1; i >= 0; i -= 1) {
    if (entries[i].kind === 'guest') return i;
  }
  return -1;
}

/** "Re-run" applies only when the last entry is a guest message. */
export function canRerun(entries) {
  const last = entries[entries.length - 1];
  return !!last && last.kind === 'guest' && last.status !== 'pending';
}

/** Total tokens for a turn's llm_usage, or null. */
export function totalTokens(usage) {
  if (!usage) return null;
  return (usage.prompt_tokens ?? 0) + (usage.completion_tokens ?? 0);
}
